// @flow

import React from 'react';
import type { Node } from 'react';
import { Section, Wrapper } from '@riseart/layout';
import type { CmsLayoutProps } from 'shared_components/cms/types';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { CmsContainer } from 'shared_components/cms/Container';
import { CmsHeadContentRenderer } from 'shared_components/cms/HeadContentRenderer';
import { renderCmsModule } from 'shared_components/cms/utils';

/**
 * CmsLayoutSections
 *
 * @param {CmsLayoutProps} props
 * @returns {Node}
 */
export const CmsLayoutSections = ({ placeholders }: CmsLayoutProps): Node => {
  const renderPlaceholderModule = renderCmsModule(placeholders);
  const sectionPlaceholders = placeholders
    .filter(({ placeholder }) => placeholder.indexOf('cmsContentSection') === 0)
    // Sort placeholders by index in the name
    .sort(
      ({ placeholder: placeholderA }, { placeholder: placeholderB }) =>
        parseInt(placeholderA.replace('cmsContentSection', ''), 10) -
        parseInt(placeholderB.replace('cmsContentSection', ''), 10),
    );
  const sectionPlaceholdersCount = sectionPlaceholders.length;
  const restPlaceholders = placeholders
    .filter(
      ({ placeholder }) =>
        placeholder.indexOf('cmsContentSection') === -1 &&
        ['cmsHeadContent', 'cmsBannerSection'].indexOf(placeholder) === -1,
    )
    .sort(({ placeholder: placeholderA }, { placeholder: placeholderB }) => {
      if (placeholderA < placeholderB) {
        return -1;
      }

      if (placeholderA > placeholderB) {
        return 1;
      }

      return 0;
    });

  return (
    <LayoutDefault scrollTopOnPathChange>
      <CmsHeadContentRenderer
        placeholderName="cmsHeadContent"
        moduleLoader={renderPlaceholderModule}
        placeholders={placeholders}
      />
      <CmsContainer>
        {renderPlaceholderModule('cmsBannerSection')}
        {/*
        Iterate over placeholders and find any placeholder name starting with cmsContentSection.
        Depending on the index it will set background to gray for every second section
        */}
        {sectionPlaceholders.map(({ placeholder, content }, idx) => {
          const parsedContent = content && JSON.parse(content);
          const renderedModule = renderPlaceholderModule(placeholder);
          const placeholderIdx = parseInt(placeholder.replace('cmsContentSection', ''), 10);

          // If content.sectionOptions.render is set to 'plain',
          // render the component without wrapping it in a Section as it includes its own Section inside
          if (
            parsedContent &&
            parsedContent.sectionOptions &&
            parsedContent.sectionOptions.render === 'plain'
          ) {
            return <React.Fragment key={idx}>{renderedModule}</React.Fragment> || null;
          }

          return renderedModule ? (
            <Section
              key={idx}
              {...(placeholderIdx % 2 !== 0 && idx < sectionPlaceholdersCount - 1
                ? { background: 'gray' }
                : null)}
            >
              <Wrapper
                sizes={{
                  xs: 'wide',
                  sm: 'narrow',
                  md: 'wide',
                  lg: 'narrow',
                  xl: 'narrow',
                }}
              >
                {renderedModule}
              </Wrapper>
            </Section>
          ) : null;
        })}
        {restPlaceholders.map(({ placeholder }) => (
          <React.Fragment key={placeholder}>{renderPlaceholderModule(placeholder)}</React.Fragment>
        ))}
      </CmsContainer>
    </LayoutDefault>
  );
};
